import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project, Session, User } from '@claude-code-api/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { ActiveSessionBanner } from '@/components/dashboard/ActiveSessionBanner';
import { DashboardSidebar } from '@/components/dashboard/DashboardSidebar';
import { RecentProjects } from '@/components/dashboard/RecentProjects';
import { DashboardSkeleton } from '@/components/dashboard/DashboardSkeleton';
import { CreateProjectButton } from '@/components/CreateProjectButton';
import { StartSessionModal } from '@/components/StartSessionModal';
import { useInterval } from '@/hooks/useInterval';

export default function DashboardPage() {
  const { t } = useTranslation('dashboard');
  // Per-section states
  const [sessions, setSessions] = useState<Session[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [_sessionsError, setSessionsError] = useState<string | null>(null);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Session modal state
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch('/api/projects', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.data || []);
        setProjectsError(null);
      } else {
        setProjectsError(t('failedToLoadProjects'));
      }
    } catch {
      setProjectsError(t('failedToLoadProjects'));
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch('/api/sessions', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.data || []);
        setSessionsError(null);
      } else {
        setSessionsError(t('failedToLoadSessions'));
      }
    } catch {
      setSessionsError(t('failedToLoadSessions'));
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data.data);
      }
    } catch {
      // Silent fail for user -- use defaults
    }
  }, []);

  // Initial load -- fire independently
  useEffect(() => {
    fetchProjects();
    fetchSessions();
    fetchUser();
  }, [fetchProjects, fetchSessions, fetchUser]);

  // Poll sessions every 10 seconds
  useInterval(() => {
    fetchSessions();
  }, 10_000);

  // Derived state
  const activeSessions = sessions.filter((s) => s.status === 'running');
  const firstName =
    user?.name?.split(' ')[0] || t('common:unknownUser', { ns: 'common' });

  // Event handlers
  const handleProjectCreated = (project: Project) => {
    setProjects((prev) => [project, ...prev]);
  };

  const handleSessionCreated = (session: Session) => {
    setSessions((prev) => [session, ...prev]);
  };

  const handleStartClick = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  const handleModalClose = () => {
    setSelectedProjectId(null);
  };

  // Initial full-page skeleton
  if (sessionsLoading && projectsLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('welcome', { name: firstName })}
        description={t('description')}
        action={<CreateProjectButton onProjectCreated={handleProjectCreated} />}
      />

      {/* Active session banner -- only when running sessions exist */}
      {activeSessions.length > 0 && (
        <ActiveSessionBanner sessions={activeSessions} projects={projects} />
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <DashboardSidebar
          sessions={sessions}
          projects={projects}
          user={user}
          loading={sessionsLoading}
        />
        <RecentProjects
          projects={projects}
          sessions={sessions}
          loading={projectsLoading}
          error={projectsError}
          onRetry={fetchProjects}
          onStartSession={handleStartClick}
          onProjectCreated={handleProjectCreated}
        />
      </div>

      {/* Start Session Modal -- triggered from project cards */}
      {selectedProjectId && (
        <StartSessionModal
          projectId={selectedProjectId}
          onClose={handleModalClose}
          onSessionCreated={handleSessionCreated}
        />
      )}
    </div>
  );
}
